import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Header } from "../components/Header";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { SourceAssetGrid } from "../components/source/SourceAssetGrid";
import { SourceFilterDrawer } from "../components/source/SourceFilterDrawer";
import { SourceSearchBar } from "../components/source/SourceSearchBar";
import {
  useCatalogCategories,
  useCatalogManufacturers,
  useSourceCatalog,
} from "../hooks/useSourceCatalog";
import { useSourceTabEnabled } from "../hooks/useSourceTabEnabled";
import { sourceSearchSchema } from "../lib/route-schemas";
import { toggleValue } from "../lib/source-catalog";

export const Route = createFileRoute("/source")({
  validateSearch: sourceSearchSchema,
  component: SourcePage,
});

function SourcePage() {
  const navigate = useNavigate();
  const sourceTab = useSourceTabEnabled();

  // Staging-only for now: an environment without the flag has no Source tab,
  // so a typed or bookmarked /source goes home like any unknown page would.
  // Replacing the entry keeps Back from returning to /source and bouncing again.
  useEffect(() => {
    if (!(sourceTab.isPending || sourceTab.enabled)) {
      navigate({ to: "/", replace: true });
    }
  }, [sourceTab.isPending, sourceTab.enabled, navigate]);

  return (
    <div className="min-h-screen bg-[var(--bg-default)]">
      <Header currentPage="source" />
      <main className="mx-auto w-full max-w-[1800px] px-8 pb-8">
        {sourceTab.enabled ? (
          <SourceCatalogPanel />
        ) : (
          <div className="flex min-h-96 items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        )}
      </main>
    </div>
  );
}

/** The Source frame: search bar, filter drawer and catalog grid. */
function SourceCatalogPanel() {
  const navigate = Route.useNavigate();
  const {
    manufacturer: manufacturers = [],
    category: categoryIds = [],
    q: query,
  } = Route.useSearch();
  const catalog = useSourceCatalog({ manufacturers, categoryIds, query });
  // Each list is counted within the other's selection and the search, so its
  // counts say what checking an option would add.
  const manufacturerList = useCatalogManufacturers(categoryIds, query);
  const categoryList = useCatalogCategories(manufacturers, query);

  // Each filter updates only its own param, dropped when empty; ``replace``
  // keeps toggling and searching out of the back-button history.
  const setFilter = (param: "manufacturer" | "category", next: string[]) =>
    navigate({
      search: (prev) => ({ ...prev, [param]: next.length ? next : undefined }),
      replace: true,
    });
  const setQuery = (next: string) =>
    navigate({
      search: (prev) => ({ ...prev, q: next || undefined }),
      replace: true,
    });

  return (
    <section className="flex h-[calc(100dvh-140px)] flex-col overflow-hidden rounded-2xl drop-shadow-[0_0_2px_rgba(32,38,44,0.6)]">
      <div className="flex h-[92px] shrink-0 items-center bg-[var(--bg-modal)] px-6">
        <SourceSearchBar key={query} query={query} onSearch={setQuery} />
      </div>
      <div className="flex min-h-0 flex-1">
        <SourceFilterDrawer
          manufacturers={{
            ...manufacturerList,
            selected: manufacturers,
            onToggle: (name) =>
              setFilter("manufacturer", toggleValue(manufacturers, name)),
            onClear: () => setFilter("manufacturer", []),
          }}
          categories={{
            ...categoryList,
            selected: categoryIds,
            onToggle: (id) =>
              setFilter("category", toggleValue(categoryIds, id)),
            onClear: () => setFilter("category", []),
          }}
        />
        <div className="min-w-0 flex-1 overflow-y-auto bg-[rgba(32,38,44,0.75)] px-6 pt-6 pb-[100px]">
          <SourceAssetGrid
            catalog={catalog}
            emptyMessage={
              query
                ? `No products match “${query}”.`
                : "No manufacturer products yet."
            }
          />
        </div>
      </div>
    </section>
  );
}
